import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Stack,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Collapse
} from '@mui/material';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { StyledCard, StyledCardHeader, StyledCardContent } from '@features/analysis/components/StyledComponents';
import {
  AnalysisConfigurationProps
} from '@features/analysis/types';
import DataSourceSelector from './DataSourceSelector';
import FocusAreaSelector from './FocusAreaSelector';
import AnalysisPreview from './AnalysisPreview';

const AnalysisConfiguration: React.FC<AnalysisConfigurationProps> = ({
  dataSources,
  focusAreas,
  onDataSourceChange,
  onFocusAreaChange,
  financialDocuments,
  selectedDocuments,
  onDocumentChange,
  loadingDocuments,
  hotelMetrics,
  selectedMetrics,
  onMetricChange,
  loadingMetrics,
  expandedPanels,
  togglePanel,
  averageMetrics,
  onRunAnalysis,
  analyzing
}) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  const steps = [
    {
      label: 'Select Data Sources',
      description: 'Choose what data to include in your analysis',
      component: 'dataSources'
    },
    {
      label: 'Choose Focus Areas',
      description: 'Select specific areas to analyze in detail',
      component: 'focusAreas'
    },
    {
      label: 'Review & Run',
      description: 'Review your configuration and start the analysis',
      component: 'preview'
    }
  ];

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  // Determine if analysis can be run
  const canRunAnalysis = dataSources.length > 0 || focusAreas.length > 0;

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <DataSourceSelector
            dataSources={dataSources}
            onDataSourceChange={onDataSourceChange}
            financialDocuments={financialDocuments}
            selectedDocuments={selectedDocuments}
            onDocumentChange={onDocumentChange}
            loadingDocuments={loadingDocuments}
            hotelMetrics={hotelMetrics}
            selectedMetrics={selectedMetrics}
            onMetricChange={onMetricChange}
            loadingMetrics={loadingMetrics}
            averageMetrics={averageMetrics}
          />
        );
      case 1:
        return (
          <FocusAreaSelector
            focusAreas={focusAreas}
            onFocusAreaChange={onFocusAreaChange}
          />
        );
      case 2:
        return (
          <AnalysisPreview
            dataSources={dataSources}
            focusAreas={focusAreas}
            selectedDocuments={selectedDocuments}
            selectedMetrics={selectedMetrics}
            financialDocuments={financialDocuments}
            hotelMetrics={hotelMetrics}
            onRunAnalysis={onRunAnalysis}
            analyzing={analyzing}
            canRunAnalysis={canRunAnalysis}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StyledCard sx={{ mb: 4 }}>
      <StyledCardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AnalyticsIcon sx={{ color: theme.palette.primary.main }} />
            Analysis Configuration
          </Box>
        }
        subheader="Configure your valuation analysis parameters"
        action={
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowPreview(!showPreview)}
            sx={{ textTransform: 'none' }}
          >
            {showPreview ? 'Hide' : 'Show'} Quick Preview
          </Button>
        }
      />
      <StyledCardContent>
        {/* Quick Preview (Collapsible) */}
        <Collapse in={showPreview}>
          <Box sx={{ mb: 3 }}>
            <AnalysisPreview
              dataSources={dataSources}
              focusAreas={focusAreas}
              selectedDocuments={selectedDocuments}
              selectedMetrics={selectedMetrics}
              financialDocuments={financialDocuments}
              hotelMetrics={hotelMetrics}
              onRunAnalysis={onRunAnalysis}
              analyzing={analyzing}
              canRunAnalysis={canRunAnalysis}
            />
          </Box>
        </Collapse>

        {/* Step-by-Step Configuration */}
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                onClick={() => handleStepClick(index)}
                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {step.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
              <StepContent>
                <Box sx={{ mt: 2, mb: 3 }}>
                  {renderStepContent(index)}
        </Box>

                {/* Step Navigation */}
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      startIcon={<NavigateBeforeIcon />}
                      variant="outlined"
                      sx={{ textTransform: 'none' }}
                    >
                      Back
                    </Button>
                    {index < steps.length - 1 && (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        endIcon={<NavigateNextIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        Next
                      </Button>
                          )}
                        </Stack>
                      </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </StyledCardContent>
    </StyledCard>
  );
};

export default AnalysisConfiguration;